/**
 * Music files by name (05 §4). Anything dropped into `/music` is found by its file name alone,
 * so adding a track never needs a code change:
 *
 *   title       the title screen and the menus behind it
 *   hiscore     initials entry and the high-score table
 *   gameover    game over, played once
 *   complete    a level finished, played once
 *   cave        in-cave music (only with In-cave music on)
 *   cave-classic, cave-deep   the same for one cave set, preferred over `cave` there
 *
 * Case, spaces, underscores, a leading track number and common misspellings do not matter:
 * `01 Game Over.MP3`, `High_Score.ogg` and `Cave-Clasic.wav` are all found. Where no file
 * answers, the synthesised track plays. Pure: names and lookups.
 */

import type { MusicTrack, MusicVariant } from './types';

/** Each word of a file name, as it is often typed -> as it is meant. */
const SPELLING: Readonly<Record<string, string>> = {
  tittle: 'title', titel: 'title', titles: 'title', tilte: 'title', theme: 'title', menu: 'title',
  highscore: 'hiscore', highscores: 'hiscore', hiscores: 'hiscore', hiscor: 'hiscore', scores: 'hiscore',
  gamover: 'gameover', gameovr: 'gameover', gamesover: 'gameover',
  completed: 'complete', complet: 'complete', compleet: 'complete', completion: 'complete', clear: 'complete',
  caves: 'cave', cavern: 'cave', caverns: 'cave', cav: 'cave',
  clasic: 'classic', classics: 'classic', clasics: 'classic', classical: 'classic', clasical: 'classic',
  deeps: 'deep', depths: 'deep', deap: 'deep',
};

/** Whole names after spelling: the multi-word ways of saying a track. */
const PHRASES: Readonly<Record<string, string>> = {
  'title-title': 'title', 'title-music': 'title', 'main-title': 'title', 'main-title-title': 'title',
  'hi-score': 'hiscore', 'high-score': 'hiscore', 'high-hiscore': 'hiscore', 'hi-hiscore': 'hiscore',
  'game-over': 'gameover', 'level-complete': 'complete', 'cave-complete': 'complete', 'stage-complete': 'complete',
  'level-clear': 'complete', 'cave-the-deep': 'cave-deep', 'the-deep': 'cave-deep', 'deep': 'cave-deep',
  'classic': 'cave-classic', 'in-cave': 'cave', 'cave-music': 'cave',
};

/** `/music/01 Game_Over.MP3` -> `gameover`: lower case, no extension, words joined by hyphens. */
export function trackName(path: string): string {
  const file = path.slice(path.lastIndexOf('/') + 1);
  const dot = file.lastIndexOf('.');
  const bare = (dot > 0 ? file.slice(0, dot) : file).trim().toLowerCase();
  const words = bare.split(/[\s_.-]+/).filter((w) => w !== '');
  // "01 Title", "track 3 - title": a numbering in front is not part of the name.
  while (words.length > 1 && /^(\d+|track)$/.test(words[0] ?? '')) words.shift();
  const name = words.map((w) => SPELLING[w] ?? w).join('-');
  return PHRASES[name] ?? name;
}

/** The names to look for, most specific first. */
export function trackNames(track: MusicTrack, variant?: MusicVariant): string[] {
  if (track === 'cave' && variant !== undefined) return [`cave-${variant}`, 'cave'];
  return [track];
}

/** Name -> address, from a map of path -> address (what `import.meta.glob` gives). */
export function trackTable(files: Readonly<Record<string, string>>): Map<string, string> {
  const table = new Map<string, string>();
  for (const [path, url] of Object.entries(files)) table.set(trackName(path), url);
  return table;
}

/** The first of `names` there is a file for. */
export function pickTrack(
  table: ReadonlyMap<string, string>, names: readonly string[],
): { name: string; url: string } | null {
  for (const name of names) {
    const url = table.get(name);
    if (url !== undefined) return { name, url };
  }
  return null;
}
